/** WinProb — всегда в автоанализе, без отдельного флага User. */
export const WINPROB_PIPELINE_META = {
  id: 'winprob',
  label: 'Вероятность победы',
  description: 'Оценка шансов на победу по документам закупки (всегда при автоанализе)',
  stageKey: 'winprob',
  alwaysOn: true,
}

/**
 * Переключатели пайплайна (6 шт.) — единый каталог для ЛК и админки.
 * Этап «Цены конкурентов» скрыт: в продукте сейчас не используется.
 * @type {Array<{
 *   id: string,
 *   field: string,
 *   label: string,
 *   description: string,
 *   stageKey: string|null,
 *   sumGroup: 'auto'|'on_demand'|'none',
 *   perMessage?: boolean,
 *   requires?: string[],
 * }>}
 */
export const PIPELINE_TOGGLE_STAGES = [
  {
    id: 'chat',
    field: 'pipeline_allow_assistant_chat',
    label: 'Чат с документами',
    description: 'Вопросы по документам тендера в чате ассистента',
    stageKey: 'chat',
    sumGroup: 'on_demand',
    perMessage: true,
  },
  {
    id: 'criteria',
    field: 'pipeline_allow_tender_criteria',
    label: 'AI-критерии закупки',
    description: 'Дерево критериев при запуске автоанализа',
    stageKey: 'criteria',
    sumGroup: 'auto',
  },
  {
    id: 'items',
    field: 'pipeline_allow_items_analysis',
    label: 'Анализ позиций',
    description: 'Номенклатура и позиции закупки',
    stageKey: 'items',
    sumGroup: 'auto',
  },
  {
    id: 'compliance',
    field: 'pipeline_allow_compliance_scan',
    label: 'Антириск-скан (44/223)',
    description: 'Проверка рисков по правилам и ИИ во вкладке «Риски»',
    stageKey: 'compliance',
    sumGroup: 'on_demand',
  },
  {
    id: 'roadmap',
    field: 'pipeline_allow_approval_roadmap',
    label: 'Дорожная карта согласования',
    description: 'Чеклист задач и сроков согласования заявки',
    stageKey: 'approval_roadmap',
    sumGroup: 'on_demand',
  },
  {
    id: 'auto_roadmap',
    field: 'pipeline_auto_roadmap_from_compliance',
    label: 'Автозадачи после скана',
    description: 'После антириска — задачи «Проверить…» для рисков high/critical',
    stageKey: null,
    sumGroup: 'none',
    requires: ['compliance', 'roadmap'],
  },
]

export const PIPELINE_TOGGLE_COUNT = PIPELINE_TOGGLE_STAGES.length

const BY_ID = Object.fromEntries(PIPELINE_TOGGLE_STAGES.map((s) => [s.id, s]))

export function isPipelineFlagEnabled(source, field) {
  if (!field) return true
  return source?.[field] !== false
}

export function countEnabledPipelineToggles(source) {
  return PIPELINE_TOGGLE_STAGES.filter((s) => isPipelineFlagEnabled(source, s.field)).length
}

export function pipelineSummaryLabel(source) {
  return `${countEnabledPipelineToggles(source)}/${PIPELINE_TOGGLE_COUNT}`
}

/** Начальные значения для reactive draft / defaults. */
export function defaultPipelineFlags() {
  const out = {}
  for (const s of PIPELINE_TOGGLE_STAGES) {
    out[s.field] = s.field === 'pipeline_auto_roadmap_from_compliance' ? false : true
  }
  return out
}

export function readPipelineFlagsFromUser(user) {
  const out = defaultPipelineFlags()
  if (!user) return out
  for (const s of PIPELINE_TOGGLE_STAGES) {
    if (s.field === 'pipeline_auto_roadmap_from_compliance') {
      out[s.field] = !!user[s.field]
    } else {
      out[s.field] = user[s.field] !== false
    }
  }
  return out
}

export function serializePipelineFlags(draft) {
  const out = {}
  for (const s of PIPELINE_TOGGLE_STAGES) {
    out[s.field] = !!draft[s.field]
  }
  // Этап не в UI: при сохранении настроек явно выключаем на бэкенде.
  out.pipeline_allow_competitor_prices = false
  return out
}

export function isAutoRoadmapAvailable(draft) {
  return (
    isPipelineFlagEnabled(draft, 'pipeline_allow_compliance_scan')
    && isPipelineFlagEnabled(draft, 'pipeline_allow_approval_roadmap')
  )
}

/** Сброс auto_roadmap, если выключены compliance или roadmap. */
export function normalizePipelineDraft(draft) {
  if (!isAutoRoadmapAvailable(draft)) {
    draft.pipeline_auto_roadmap_from_compliance = false
  }
  return draft
}

/**
 * Ориентировочные токены для блока «Настройки AI-анализа» в ЛК (MVP).
 * Не из БД; админка по-прежнему может использовать GET /billing/pipeline-stage-hints/.
 */
export const PIPELINE_MVP_TOKEN_HINTS = {
  stages: {
    criteria: 120,
    items: 90,
    winprob: 45,
    chat: 8,
    compliance: 85,
    approval_roadmap: 70,
  },
}

export function stageHintTokens(hints, stageKey) {
  if (!stageKey || !hints?.stages) return null
  const v = hints.stages[stageKey]
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null
}

export function formatTokenHint(n) {
  const v = Number(n)
  if (!Number.isFinite(v) || v <= 0) return '—'
  return `~${new Intl.NumberFormat('ru-RU').format(Math.round(v))}`
}

/**
 * @param {object} draft — флаги
 * @param {{ stages?: Record<string, number> }} hints
 */
export function computePipelineTokenTotals(draft, hints = {}) {
  let autoAnalysis = 0
  let onDemand = 0
  let chatPerMessage = null

  for (const s of PIPELINE_TOGGLE_STAGES) {
    if (!isPipelineFlagEnabled(draft, s.field)) continue
    const tokens = stageHintTokens(hints, s.stageKey)
    if (s.sumGroup === 'auto' && tokens != null) autoAnalysis += tokens
    if (s.sumGroup === 'on_demand') {
      if (s.perMessage) chatPerMessage = tokens
      else if (tokens != null) onDemand += tokens
    }
  }

  const winprob = stageHintTokens(hints, WINPROB_PIPELINE_META.stageKey)
  if (winprob != null) autoAnalysis += winprob

  return {
    autoAnalysis,
    onDemand,
    chatPerMessage,
    fullCycle: autoAnalysis + onDemand,
    winprob,
  }
}

export function getStageById(id) {
  return BY_ID[id] || null
}

/** Вкладка модалки тендера → флаг. */
export const TENDER_TAB_PIPELINE_FIELDS = {
  criteria: 'pipeline_allow_tender_criteria',
  products: 'pipeline_allow_items_analysis',
  compliance: 'pipeline_allow_compliance_scan',
  roadmap: 'pipeline_allow_approval_roadmap',
  'ask-ai': 'pipeline_allow_assistant_chat',
}

export function isTenderTabAllowed(tabKey, user) {
  const field = TENDER_TAB_PIPELINE_FIELDS[tabKey]
  if (!field) return true
  return isPipelineFlagEnabled(user, field)
}
